import { useEffect, useState } from 'react';
import { Send, User } from 'lucide-react';
import { useModernChatStore } from '../../../stores/modern-chat-store';
import { Button } from '../../ui/Button';
import { Bot } from 'lucide-react';

interface ModernInputAreaProps {
  playbookId: string;
  playbookName?: string;
  userId?: string;
}

function ModernInputArea({ playbookId, playbookName, userId }: ModernInputAreaProps) {
  const sendChatMessageAsync = useModernChatStore((state) => state.sendChatMessageAsync);
  const pendingResponse = useModernChatStore((state) => state.pendingResponse);
  const initChatWebSocket = useModernChatStore((state) => state.initChatWebSocket);
  const currWebSocket = useModernChatStore((state) => state.currWebSocket);
  const isConnecting = useModernChatStore((state) => state.isConnecting);
  const isPlaybookReady = useModernChatStore((state) => state.isPlaybookReady);
  const connectionError = useModernChatStore((state) => state.connectionError);
  const [message, setMessage] = useState('');

  useEffect(() => {
    console.log('🚀 Initializing chat for playbook:', playbookName || playbookId);
    console.log('📋 Full playbook details:', { playbookId, playbookName, userId });
    
    const chatConfig = {
      playbookId,
      playbookName,
      userId,
      useDynamic: true
    };
    
    console.log('📤 Sending WebSocket config:', chatConfig);
    initChatWebSocket(chatConfig);
    
    return () => {
      try {
        if (currWebSocket) {
          console.log('🔌 Cleaning up WebSocket connection');
          currWebSocket.close();
        }
      } catch (err) {
        console.log('Error closing WebSocket:', err);
      }
    };
  }, [initChatWebSocket, playbookId, playbookName, userId]);

  const handleSendMessage = async () => {
    if (message.trim().length === 0 || !isPlaybookReady || isConnecting) return;
    setMessage('');
    sendChatMessageAsync(message);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Determine input state
  const isInputDisabled = pendingResponse || isConnecting || !isPlaybookReady;
  const getPlaceholderText = () => {
    if (isConnecting) return "Connecting to chat service...";
    if (!isPlaybookReady) return "Loading playbook...";
    if (pendingResponse) return "Agent is thinking...";
    return "Type your message...";
  };

  return (
    <div className="bg-white border-t border-gray-100 p-4 backdrop-blur-sm bg-white/95 z-10 max-h-[200px] min-h-[80px]">
      <div className="max-w-4xl mx-auto">
        {/* Connection Error Banner */}
        {connectionError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            ⚠️ {connectionError}
          </div>
        )}
        
        {/* Loading State Banner */}
        {(isConnecting || !isPlaybookReady) && !connectionError && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            {isConnecting ? 'Connecting to chat service...' : 'Loading playbook...'}
          </div>
        )}
        
        <div className="flex items-end gap-3 p-4 bg-gray-50/80 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-gray-300 focus-within:shadow-md">
          <div className="flex-1 min-h-[40px] max-h-32">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholderText()}
              disabled={isInputDisabled}
              rows={1}
              className="w-full bg-transparent border-none outline-none resize-none text-gray-900 placeholder-gray-500 text-base leading-relaxed py-2 disabled:opacity-50"
              style={{
                minHeight: '24px',
                maxHeight: '120px',
                overflow: 'auto',
                fontFamily: 'inherit'
              }}
            />
          </div>
          
          <Button
            onClick={handleSendMessage}
            disabled={isInputDisabled || message.trim().length === 0}
            size="sm"
            className={`
              w-10 h-10 p-0 rounded-xl transition-all duration-200 flex items-center justify-center
              ${message.trim().length > 0 && !isInputDisabled 
                ? 'bg-black hover:bg-gray-800 text-white shadow-md hover:shadow-lg transform hover:scale-105' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {isConnecting || (!isPlaybookReady && !connectionError) ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : !pendingResponse ? (
              <Send className="w-4 h-4" />
            ) : (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            )}
          </Button>
        </div>
        
      </div>
    </div>
  );
}

interface ModernChatDialogProps {
  playbookId: string;
  playbookName?: string;
  userId?: string;
}

// Helper functions to extract complex conditions and logic
const isUserMessage = (msgFrom: string): boolean => msgFrom === 'user';
const isAssistantMessage = (msgFrom: string): boolean => msgFrom === 'gpt';

const shouldShowLoadingState = (isConnecting: boolean, isPlaybookReady: boolean, connectionError: string | null): boolean => {
  return (isConnecting || !isPlaybookReady) && !connectionError;
};

const shouldShowErrorState = (connectionError: string | null): boolean => {
  return !!connectionError;
};

const getWelcomeMessage = (currentConfig: any, playbookName?: string): string => {
  if (currentConfig?.playbookId) {
    return "Let's start a conversation. I'll help you with " + (playbookName || 'your request') + ".";
  }
  return "I'm here to help you with your queries.";
};

const getPlaybookReadyText = (currentConfig: any): string => {
  if (!currentConfig?.playbookId) return '';
  return `✅ Playbook Ready: ${currentConfig.playbookId.split('-')[1]}...`;
};

const getThinkingText = (playbookName?: string, currentConfig?: any): string => {
  return `${playbookName || currentConfig?.playbookName || 'Assistant'} is thinking...`;
};

// Extracted components for better separation of concerns
const LoadingState: React.FC<{
  isConnecting: boolean;
  playbookName?: string;
}> = ({ isConnecting, playbookName }) => (
  <>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      <h3 className="text-2xl font-bold text-gray-800">
        {isConnecting ? 'Connecting...' : 'Loading Playbook...'}
      </h3>
    </div>
    <p className="text-gray-600 text-lg max-w-md leading-relaxed">
      {isConnecting 
        ? 'Establishing connection to chat service...'
        : `Loading ${playbookName || 'your playbook'}... This may take a moment.`
      }
    </p>
  </>
);

const ErrorState: React.FC<{
  connectionError: string;
}> = ({ connectionError }) => (
  <>
    <h3 className="text-2xl font-bold text-red-600 mb-3">
      Connection Error
    </h3>
    <p className="text-gray-600 text-lg max-w-md leading-relaxed mb-4">
      {connectionError}
    </p>
    <button 
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
    >
      Refresh Page
    </button>
  </>
);

const WelcomeState: React.FC<{
  playbookName?: string;
  currentConfig: any;
}> = ({ playbookName, currentConfig }) => (
  <>
    <h3 className="text-2xl font-bold text-gray-800 mb-3">
      Welcome to {playbookName || 'Chat Assistant'}
    </h3>
    <p className="text-gray-600 text-lg max-w-md leading-relaxed">
      {getWelcomeMessage(currentConfig, playbookName)}
    </p>
    <div className="mt-4 text-sm text-gray-500">
      {currentConfig?.playbookId && (
        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
          {getPlaybookReadyText(currentConfig)}
        </span>
      )}
    </div>
  </>
);

const EmptyState: React.FC<{
  isConnecting: boolean;
  isPlaybookReady: boolean;
  connectionError: string | null;
  playbookName?: string;
  currentConfig: any;
}> = ({ isConnecting, isPlaybookReady, connectionError, playbookName, currentConfig }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center">
    <div className="w-20 h-20 mb-6 opacity-80">
      <div className={`w-full h-full bg-blue-100 rounded-full flex items-center justify-center drop-shadow-lg ${
          isConnecting || !isPlaybookReady ? 'animate-pulse' : ''
        }`}>
        <Bot size={40} className="text-blue-600" />
      </div>
    </div>
    
    {shouldShowLoadingState(isConnecting, isPlaybookReady, connectionError) ? (
      <LoadingState isConnecting={isConnecting} playbookName={playbookName} />
    ) : shouldShowErrorState(connectionError) ? (
      <ErrorState connectionError={connectionError!} />
    ) : (
      <WelcomeState playbookName={playbookName} currentConfig={currentConfig} />
    )}
  </div>
);

const MessageAvatar: React.FC<{
  msgFrom: string;
}> = ({ msgFrom }) => {
  if (msgFrom === 'gpt') {
    return (
      <div className="w-10 h-10 rounded-full border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 flex-shrink-0 flex items-center justify-center shadow-sm">
        <Bot size={20} className="text-blue-600" />
      </div>
    );
  }
  
  if (msgFrom === 'user') {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border-2 border-gray-300 flex items-center justify-center flex-shrink-0 shadow-sm">
        <User className="w-5 h-5 text-white" />
      </div>
    );
  }
  
  return null;
};

const MessageBubble: React.FC<{
  message: string;
  msgFrom: string;
}> = ({ message, msgFrom }) => (
  <div
    className={`
      max-w-[75%] lg:max-w-[60%] px-5 py-3 rounded-2xl shadow-sm transition-all duration-200 group-hover:shadow-md
      ${msgFrom === 'user'
        ? 'bg-black text-white rounded-br-md shadow-md'
        : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
      }
    `}
  >
    <p className="text-base leading-relaxed whitespace-pre-wrap break-words">
      {message}
    </p>
    
    <div className={`text-xs mt-2 ${msgFrom === 'user' ? 'text-gray-300' : 'text-gray-400'}`}>
      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </div>
  </div>
);

const TypingIndicator: React.FC<{
  playbookName?: string;
  currentConfig: any;
}> = ({ playbookName, currentConfig }) => (
  <div className="flex items-start gap-4 justify-start animate-fade-in">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 flex-shrink-0 flex items-center justify-center shadow-sm">
      <Bot size={20} className="text-blue-600" />
    </div>

    <div className="bg-white text-gray-900 rounded-2xl rounded-bl-md border border-gray-200 px-5 py-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
              style={{
                animationDelay: `${i * 0.2}s`,
                animationDuration: '1.4s'
              }}
            />
          ))}
        </div>
        <span className="text-sm text-gray-500 italic">
          {getThinkingText(playbookName, currentConfig)}
        </span>
      </div>
    </div>
  </div>
);

const ChatMessages: React.FC<{
  chatMessages: any[];
  pendingResponse: boolean;
  playbookName?: string;
  currentConfig: any;
}> = ({ chatMessages, pendingResponse, playbookName, currentConfig }) => (
  <div className="space-y-6">
    {chatMessages.map(({ id, message, msg_from }) => (
      <div
        key={id}
        className={`flex items-start gap-4 group ${
          isUserMessage(msg_from) ? 'justify-end' : 'justify-start'
        }`}
      >
        {!isUserMessage(msg_from) && <MessageAvatar msgFrom={msg_from} />}
        <MessageBubble message={message} msgFrom={msg_from} />
        {isUserMessage(msg_from) && <MessageAvatar msgFrom={msg_from} />}
      </div>
    ))}

    {pendingResponse && (
      <TypingIndicator playbookName={playbookName} currentConfig={currentConfig} />
    )}

    <div className="h-8"></div>
  </div>
);

const ModernChatDialog: React.FC<ModernChatDialogProps> = ({ playbookId, playbookName, userId }) => {
  const chatMessages = useModernChatStore((state) => state.chatMessages);
  const pendingResponse = useModernChatStore((state) => state.pendingResponse);
  const currentConfig = useModernChatStore((state) => state.currentConfig);
  const isConnecting = useModernChatStore((state) => state.isConnecting);
  const isPlaybookReady = useModernChatStore((state) => state.isPlaybookReady);
  const connectionError = useModernChatStore((state) => state.connectionError);

  return (
    <div className="flex flex-col h-full w-full relative">
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto px-4 py-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {chatMessages.length === 0 ? (
              <EmptyState
                isConnecting={isConnecting}
                isPlaybookReady={isPlaybookReady}
                connectionError={connectionError}
                playbookName={playbookName}
                currentConfig={currentConfig}
              />
            ) : (
              <ChatMessages
                chatMessages={chatMessages}
                pendingResponse={pendingResponse}
                playbookName={playbookName}
                currentConfig={currentConfig}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex-shrink-0">
        <ModernInputArea 
          playbookId={playbookId}
          playbookName={playbookName}
          userId={userId}
        />
      </div>
    </div>
  );
};

export default ModernChatDialog; 